<template>
  <div class="wx-line-chart">
    <div class="toolbar">
      <div class="time-range">
        <wx-date-select v-model="dateRange" @change="onDateChange"></wx-date-select>
        <Button class="reset-btn" @click="resetDate">重置</Button>
        <span class="tip">（说明：分数越高表示病越少）</span>
      </div>
    </div>

    <div class="chart-grid">
      <div
        v-for="wx in wxList"
        :key="wx.name"
        class="chart-item"
      >
        <div class="chart-header">
          <span class="color-bar" :style="{ background: wx.color }"></span>
          <span class="chart-title">{{ wx.name }}行分数趋势</span>
        </div>
        <div :id="'chart-' + wx.name" class="chart-container"></div>
      </div>
    </div>

    <div v-if="loading" class="loading-mask">
      <Spin fix></Spin>
    </div>
    <div v-if="!loading && !hasData" class="empty-tip">暂无数据</div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import dayjs from "dayjs";
import wxDateSelect from "./wxDateSelect.vue";

// 五行配置：名称、颜色
const WX_CONFIG = [
  { name: "木", color: "#52c41a" },
  { name: "火", color: "#f5222d" },
  { name: "土", color: "#fa8c16" },
  { name: "金", color: "#8c8c8c" },
  { name: "水", color: "#1890ff" },
];

export default {
  name: "WxLineChart",
  components: { wxDateSelect },
  props: {

    patientCode: {
      type: String,
      default: "",
    },
    defaultDays: {
      type: Number,
      default: 90,
    },
  },
  data() {
    const defaultStart = dayjs()
      .subtract(this.defaultDays, "day")
      .format("YYYY-MM-DD");
    const defaultEnd = dayjs().format("YYYY-MM-DD");
    return {
      chartInstances: {},
      loading: false,
      hasData: true,
      dateRange: [defaultStart, defaultEnd],
      defaultRange: [defaultStart, defaultEnd],
      allData: [], // 原始接口返回数据
      wxList: JSON.parse(JSON.stringify(WX_CONFIG)),
      resizeHandler: null,
    };
  },
  watch: {
    patientCode(newVal) {
      if (newVal) {
        this.fetchData();
      }
    },
  },
  mounted() {
    if (this.patientCode) {
      this.fetchData();
    }
    this.resizeHandler = () => {
      Object.values(this.chartInstances).forEach((chart) => {
        chart && chart.resize();
      });
    };
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeHandler);
    Object.values(this.chartInstances).forEach((chart) => {
      chart && chart.dispose();
    });
    this.chartInstances = {};
  },
  methods: {
    onDateChange(value) {
      if (value && value[0] && value[1]) {
        this.dateRange = [
          dayjs(value[0]).format("YYYY-MM-DD"),
          dayjs(value[1]).format("YYYY-MM-DD"),
        ];
        this.fetchData();
      }
    },
    resetDate() {
      this.dateRange = [...this.defaultRange];
      this.fetchData();
    },
    fetchData() {
      if (!this.patientCode) return;
      this.loading = true;
      this.api2("wxLineChart", {
        patientCode: this.patientCode,
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
      })
        .then((res) => {
          const list = res.result || [];

          // 测试
          // list.push({
          //   "exceedPercentage": -1,
          //   "visitDate": "2026-09-20",
          //   "wxName": "木",
          //   "wxScore":0.32 
          // });
          
          this.allData = list;
          this.hasData = list && list.length > 0;
          this.$nextTick(() => {
            this.renderCharts();
          });
        })
        .catch(() => {
          this.hasData = false;
          this.allData = [];
          this.$nextTick(() => {
            this.renderCharts();
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 按五行分组数据
    groupByWx() {
      const groups = {};
      this.wxList.forEach((wx) => {
        groups[wx.name] = {
          ...wx,
          points: [], // { visitDate, wxScore, exceedPercentage }
        };
      });
      // 按就诊日期排序的X轴数据（去重）
      const visitDates = new Set();

      (this.allData || []).forEach((item) => {
        if (groups[item.wxName]) {
          const score = this.formatScore(item.wxScore);
          groups[item.wxName].points.push({
            visitDate: item.visitDate,
            wxScore: score,
            exceedPercentage: item.exceedPercentage,
          });
          visitDates.add(item.visitDate);
        }
      });

      // 每个五行内按日期排序
      const sortedDates = Array.from(visitDates).sort((a, b) => {
        return dayjs(a).valueOf() - dayjs(b).valueOf();
      });

      Object.values(groups).forEach((group) => {
        group.points.sort((a, b) => {
          return dayjs(a.visitDate).valueOf() - dayjs(b.visitDate).valueOf();
        });
      });

      return { groups, sortedDates };
    },
    // 将4位小数截断为2位
    formatScore(score) {
      if (score === null || score === undefined || score === "") return null;
      const num = Number(score);
      if (isNaN(num)) return null;
      // 截断（不是四舍五入）到2位小数
      return Math.trunc(num * 100) / 100;
    },
    // 渲染所有图表
    renderCharts() {
      const { groups, sortedDates } = this.groupByWx();

      // 销毁旧的
      Object.keys(this.chartInstances).forEach((name) => {
        const oldChart = this.chartInstances[name];
        if (oldChart) {
          oldChart.dispose();
        }
      });
      this.chartInstances = {};

      this.wxList.forEach((wx) => {
        const domId = "chart-" + wx.name;
        const dom = document.getElementById(domId);
        if (!dom) return;

        const chart = echarts.init(dom);
        this.chartInstances[wx.name] = chart;

        const group = groups[wx.name];
        const option = this.buildOption(wx, group, sortedDates);
        chart.setOption(option);
      });
    },
    // 构建单个图表的 option
    buildOption(wx, group, sortedDates) {
      const dates = sortedDates.map((d) => {
        // 截取 MM-DD
        if (d && d.length >= 10) return d.substring(5, 10);
        return d;
      });

      // 按 sortedDates 顺序填充数据
      const scoreMap = {};
      const exceedMap = {};
      if (group && group.points) {
        group.points.forEach((p) => {
          scoreMap[p.visitDate] = p.wxScore;
          exceedMap[p.visitDate] = p.exceedPercentage;
        });
      }

      const dataValues = sortedDates.map((d) => {
        return scoreMap[d] !== undefined ? scoreMap[d] : null;
      });
      const exceedValues = sortedDates.map((d) => {
        return exceedMap[d] !== undefined ? exceedMap[d] : null;
      });

      // Y轴最大值按数据动态计算：向上取整并预留1个单位给顶部标签
      const validScores = dataValues.filter((v) => v !== null && !isNaN(v));
      const maxScore = validScores.length ? Math.max.apply(null, validScores) : 0;
      const yMax = validScores.length ? Math.ceil(maxScore + 1) : 10;

      return {
        grid: {
          top: 20,
          right: 20,
          bottom: 20,
          left: 35,
          containLabel: false,
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "line",
          },
          formatter: (params) => {
            const idx = params[0].dataIndex;
            const wxScore = dataValues[idx];
            const exceed = exceedValues[idx];
            const date = sortedDates[idx];
            let exceedText = "";
            if (exceed === -1 || exceed === null || exceed === undefined) {
              exceedText = "暂无对比";
            } else {
              exceedText = `超越${exceed}%`;
            }
            return `<div style="font-size:12px;">
              <div>${date}</div>
              <div style="margin-top:4px;">分数：${wxScore !== null ? wxScore : "暂无数据"}</div>
              <div>${exceedText}</div>
            </div>`;
          },
        },
        xAxis: {
          type: "category",
          data: dates,
          boundaryGap: true,
          axisLabel: {
            color: "#999",
            fontSize: 11,
            margin: 10,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "#666",
              width: 1,
            },
          },
          axisTick: {
            show: true,
            alignWithLabel: true,
            // 负值使刻度线朝绘图区内（向上）延伸
            length: -6,
            lineStyle: {
              color: "#666",
              width: 1,
            },
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          // max: yMax,
          max: 10,
          splitNumber: 5,
          axisLabel: {
            color: "#999",
            fontSize: 11,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            lineStyle: {
              color: "#f0f0f0",
              type: "dashed",
            },
          },
        },
        series: [
          {
            name: wx.name + "行",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            connectNulls: false,
            itemStyle: {
              color: wx.color,
            },
            lineStyle: {
              color: wx.color,
              width: 2,
            },
            label: {
              show: true,
              position: "top",
              formatter: (params) => {
                const score = dataValues[params.dataIndex];
                return score !== null ? score : "";
              },
              color: wx.color,
              fontSize: 11,
              fontWeight: "bold",
            },
            data: dataValues,
          },
          {
            name: wx.name + "行-超越",
            type: "scatter",
            symbolSize: 0,
            silent: true,
            label: {
              show: true,
              position: "bottom",
              formatter: (params) => {
                const idx = params.dataIndex;
                const exceed = exceedValues[idx];
                if (
                  dataValues[idx] === null ||
                  exceed === null ||
                  exceed === undefined
                ) {
                  return "";
                }
                if (exceed === -1) {
                  return "暂无对比";
                }
                return `超越${exceed}%`;
              },
              color: wx.color,
              fontSize: 10,
            },
            data: dataValues,
          },
        ],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.wx-line-chart {
  position: relative;
  .toolbar {
    margin-bottom: 15px;

    .time-range {
      display: flex;
      align-items: center;
      gap: 12px;
      line-height: 32px;
      height: 32px;

      .reset-btn {
        min-width: 60px;
      }

      .tip {
        font-size: 13px;
        color: #999;
      }
    }
  }

  .chart-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 1fr;
    gap: 16px;

    .chart-item {
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      padding: 12px;
      background: #fafafa;

      .chart-header {
        display: flex;
        align-items: center;
        gap: 8px;
        line-height: 20px ;

        .color-bar {
          display: inline-block;
          width: 3px;
          height: 14px;
          border-radius: 2px;
        }

        .chart-title {
          font-size: 14px;
          font-weight: bold;
          color: #333;
        }
      }

      .chart-container {
        width: 100%;
        height: 220px;
      }
    }

    @media screen and (max-width: 1400px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media screen and (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .loading-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.6);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty-tip {
    text-align: center;
    color: #999;
    padding: 40px 0;
    font-size: 14px;
  }
}
</style>
